import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import SEO from '../components/SEO';
import { getBlogPost, blogPosts } from '../data/blogPosts';
import { ArrowLeft, Clock, ArrowRight, ShoppingBag } from 'lucide-react';
import { buildCheckoutUrl } from '../constants';

const PRODUCT_URL = buildCheckoutUrl(1);

function buildArticleSchema(post) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      '@type': 'Organization',
      name: 'RELOAD Nutrition',
      url: 'https://reload-nutrition.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'RELOAD Nutrition',
      url: 'https://reload-nutrition.com',
    },
    image: post.image,
    mainEntityOfPage: `https://reload-nutrition.com/blog/${post.slug}`,
    keywords: post.keywords,
  };
}

function renderContent(text) {
  return text.split('\n\n').map((para, i) => {
    if (para.startsWith('**') && para.includes('**:')) {
      const colonIdx = para.indexOf(':');
      const boldPart = para.substring(2, colonIdx - 2);
      const rest = para.substring(colonIdx + 1).trim();
      return (
        <p key={i} style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.96rem', color: '#475569', lineHeight: 1.85, marginBottom: '16px' }}>
          <strong style={{ color: '#0D1A2E', fontWeight: 700 }}>{boldPart}:</strong> {rest}
        </p>
      );
    }
    if (para.startsWith('- ')) {
      const items = para.split('\n').filter((l) => l.startsWith('- ')).map((l) => l.slice(2));
      return (
        <ul key={i} style={{ marginBottom: '16px', paddingLeft: '20px' }}>
          {items.map((item, j) => (
            <li key={j} style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.96rem', color: '#475569', lineHeight: 1.85, marginBottom: '6px', listStyleType: 'disc' }}>
              {item}
            </li>
          ))}
        </ul>
      );
    }
    // Replace HTML entities-like patterns left in strings
    return (
      <p key={i} style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.96rem', color: '#475569', lineHeight: 1.85, marginBottom: '16px' }}>
        {para}
      </p>
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogPost(slug);
  useEffect(() => {
    if (!post) navigate('/blog', { replace: true });
  }, [post, navigate]);

  if (!post) return null;

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <SEO
        title={post.title}
        description={post.metaDescription}
        canonical={`/blog/${post.slug}`}
        schema={buildArticleSchema(post)}
        ogImage={post.image}
      />

      {/* Back nav */}
      <div style={{ backgroundColor: '#FFFFFF', borderBottom: '1px solid #E2E8F0', padding: '14px 0' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            data-testid="blog-post-back-link"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: 'Manrope, sans-serif', fontSize: '0.78rem', fontWeight: 600, color: '#64748B', textDecoration: 'none', letterSpacing: '0.05em', transition: 'color 0.2s' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#1B3D7E'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#64748B'}>
            <ArrowLeft size={14} /> Back to Blog
          </Link>
        </div>
      </div>

      {/* Hero image + title */}
      <div style={{ backgroundColor: '#F7F7F5' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-12 pb-8">
          {/* Meta */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <span style={{ backgroundColor: '#EEF2FF', color: '#1B3D7E', fontSize: '0.68rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 12px', borderRadius: '100px' }}>
              {post.category}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94A3B8', fontSize: '0.75rem', fontFamily: 'Manrope, sans-serif' }}>
              <Clock size={12} /> {post.readTime}
            </span>
            <span style={{ color: '#94A3B8', fontSize: '0.75rem', fontFamily: 'Manrope, sans-serif' }}>
              {post.date}
            </span>
          </div>

          {/* Title */}
          <h1
            data-testid="blog-post-title"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
              color: '#0D1A2E',
              lineHeight: 1.15,
              marginBottom: '16px',
            }}>
            {post.title}
          </h1>
          <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '1.05rem', color: '#64748B', lineHeight: 1.7, marginBottom: '32px' }}>
            {post.excerpt}
          </p>
        </div>

        {/* Cover image */}
        <div style={{ maxWidth: '900px', margin: '0 auto', paddingLeft: '24px', paddingRight: '24px', paddingBottom: '0' }}>
          <div style={{ borderRadius: '16px', overflow: 'hidden', maxHeight: '440px' }}>
            <img
              src={post.image}
              alt={post.title}
              data-testid="blog-post-image"
              style={{ width: '100%', height: '440px', objectFit: 'cover', display: 'block' }}
            />
          </div>
        </div>
      </div>

      {/* Article body */}
      <article
        data-testid="blog-post-body"
        style={{ backgroundColor: '#FFFFFF', padding: '64px 0 80px' }}>
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {post.sections.map((section, i) => (
            <div key={i} style={{ marginBottom: '44px' }}>
              <h2 style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(1.1rem, 2vw, 1.45rem)',
                color: '#0D1A2E',
                lineHeight: 1.3,
                marginBottom: '14px',
                paddingTop: i > 0 ? '8px' : '0',
                borderTop: i > 0 ? '1px solid #E2E8F0' : 'none',
              }}>
                {section.heading}
              </h2>
              {renderContent(section.content)}
            </div>
          ))}

          {/* Inline product mention */}
          <div
            data-testid="blog-post-inline-cta"
            style={{
              backgroundColor: '#EEF2FF',
              border: '1px solid #C7D7F8',
              borderRadius: '12px',
              padding: '28px 32px',
              marginTop: '48px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem', color: '#1B3D7E', fontWeight: 700, margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
              RELOAD Nutrition
            </p>
            <p style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1.2rem', color: '#0D1A2E', margin: 0, lineHeight: 1.3 }}>
              The all-in-one formula for semen volume, libido &amp; male vitality
            </p>
            <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: '0.88rem', color: '#475569', margin: 0, lineHeight: 1.65 }}>
              Pygeum Africanum, Catuaba Bark, Korean Ginseng, Zinc &amp; Selenium — in one premium daily capsule.
            </p>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '4px' }}>
              <a
                href={PRODUCT_URL}
                data-testid="blog-post-buy-cta"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-blue"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 24px', borderRadius: '100px', textDecoration: 'none', fontSize: '0.82rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
                <ShoppingBag size={14} /> Buy RELOAD — £24.99
              </a>
              <Link
                to="/shop"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '12px 22px', borderRadius: '100px', textDecoration: 'none', fontSize: '0.82rem', fontFamily: 'Manrope, sans-serif', fontWeight: 600, color: '#1B3D7E', border: '1.5px solid #1B3D7E', transition: 'background 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1B3D7E'; e.currentTarget.style.color = '#FFFFFF'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#1B3D7E'; }}>
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related articles */}
      {otherPosts.length > 0 && (
        <section
          data-testid="blog-post-related"
          style={{ backgroundColor: '#F7F7F5', padding: '64px 0 80px', borderTop: '1px solid #E2E8F0' }}>
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <h2 style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 800,
              fontSize: '1.4rem',
              color: '#0D1A2E',
              marginBottom: '32px',
            }}>
              More Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {otherPosts.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  data-testid={`related-post-${related.slug}`}
                  style={{ textDecoration: 'none' }}>
                  <div
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '14px',
                      overflow: 'hidden',
                      border: '1px solid #E2E8F0',
                      transition: 'box-shadow 0.25s, transform 0.25s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 10px 32px rgba(27,61,126,0.10)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    <div style={{ height: '180px', overflow: 'hidden' }}>
                      <img src={related.image} alt={related.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                    <div style={{ padding: '22px 24px 26px' }}>
                      <span style={{ backgroundColor: '#EEF2FF', color: '#1B3D7E', fontSize: '0.63rem', fontFamily: 'Manrope, sans-serif', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: '100px', display: 'inline-block', marginBottom: '10px' }}>
                        {related.category}
                      </span>
                      <h3 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#0D1A2E', lineHeight: 1.3, marginBottom: '8px' }}>
                        {related.title}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#1B3D7E', fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: '0.76rem', marginTop: '8px' }}>
                        Read Article <ArrowRight size={12} />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
